import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { staffApi, formatApiError, formatBranchHours } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { LogOut, LayoutGrid, ChevronRight } from "lucide-react";
import Logo from "@/components/layout/Logo";
import { isOwner, calendarViewScope } from "@/lib/permissions";
import { hasAnyBrandPermission } from "@/App";
import { resolveBranchTheme } from "@/lib/branchTheme";

// Iter-378 · Branch cards now use the Cocoon butterfly mark instead of
// a generic building glyph. The PNG is treated as a mask so the
// silhouette inherits whatever `backgroundColor` we feed it — letting
// each card tint itself with the branch's primary HSL (burgundy for
// Legian, gold for Bahrain, etc.) without needing per-branch assets.
const COCOON_MARK_SRC = "/brand/cocoon-mark.png";
const cocoonMarkStyle = (hsl) => ({
  backgroundColor: `hsl(${hsl})`,
  WebkitMaskImage: `url(${COCOON_MARK_SRC})`,
  maskImage: `url(${COCOON_MARK_SRC})`,
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskPosition: "center",
  maskPosition: "center",
  WebkitMaskSize: "contain",
  maskSize: "contain",
});

const roleLabel = (staff) => staff?.role_detail?.name || staff?.role || "";

export default function BranchPicker() {
  const { staff, logoutStaff } = useAuth();
  const nav = useNavigate();
  const [branches, setBranches] = useState([]);

  // Non-Owners only see branches they've been assigned to. Everyone else
  // (Owner) sees the full list. This mirrors the backend `/branches` scope
  // so the picker never shows a card the user can't open, and disappears
  // entirely for staff with zero branch assignments.
  const visibleBranches = isOwner(staff)
    ? branches
    : branches.filter((b) => (staff?.branch_ids || []).includes(b.id));

  // Personal-view-only practitioners belong on the mobile roster page.
  // Auto-redirect so a direct visit to /staff/branches (bookmark, back
  // button, "All views" link elsewhere) still lands them where they need
  // to be. Owners and global-view roles fall through to the picker.
  useEffect(() => {
    if (!staff) return;
    const scope = calendarViewScope(staff);
    if (!isOwner(staff) && scope.personal && !scope.global) {
      nav("/staff/my-roster", { replace: true });
    }
  }, [staff, nav]);

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranches(r.data)).catch((err) => toast.error(formatApiError(err)));
  }, []);

  const allowed = (b) => isOwner(staff) || (staff?.branch_ids || []).includes(b.id);

  const openBranch = (b) => {
    if (!allowed(b)) return;
    nav(`/staff/branch/${b.id}/calendar`);
  };

  return (
    <div className="min-h-screen bg-[hsl(40_20%_97%)]">
      <header className="h-14 px-6 border-b border-border bg-white flex items-center justify-between">
        <Logo variant="mark" to={null} size={36} testid="branchpicker-brand-logo" />
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{staff?.full_name} · {roleLabel(staff)}</span>
          <Button variant="ghost" size="sm" onClick={() => { logoutStaff(); nav("/staff/login"); }} data-testid="branchpicker-logout-btn"><LogOut className="w-4 h-4" /></Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-16">
        <p className="tracking-overline text-xs text-muted-foreground">STEP ONE</p>
        <h1 className="text-3xl lg:text-4xl font-semibold mt-2 mb-2">Choose your workspace</h1>
        <p className="text-sm text-muted-foreground mb-10">Tap a location to open its workspace, or open the brand workspace to manage the catalog and cross-branch reports.</p>

        {hasAnyBrandPermission(staff) && (
          <button
            type="button"
            onClick={() => nav("/staff/brand")}
            data-testid="brand-workspace-card"
            className="w-full text-left mb-8 group"
          >
            <Card className="rounded-xl border-primary/40 transition-all duration-200 group-hover:shadow-md group-hover:border-primary group-active:scale-[0.995]">
              <CardContent className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center"><LayoutGrid className="w-5 h-5" /></div>
                  <div>
                    <p className="font-semibold">Brand Workspace</p>
                    <p className="text-xs text-muted-foreground">Catalog · Reports · Staff logins · Cross-branch settings</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </CardContent>
            </Card>
          </button>
        )}

        <p className="tracking-overline text-xs text-muted-foreground mb-3">LOCATIONS</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {visibleBranches.length === 0 && (
            <p className="text-sm text-muted-foreground italic" data-testid="branch-picker-empty">
              No locations assigned to your account yet. Contact the operator for access.
            </p>
          )}
          {visibleBranches.map((b) => {
            const can = allowed(b);
            const theme = resolveBranchTheme(b.name);
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => openBranch(b)}
                disabled={!can}
                data-testid={`branch-row-${b.name.toLowerCase()}`}
                className={`text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl ${can ? "" : "opacity-50 cursor-not-allowed"}`}
              >
                <Card className={`rounded-xl transition-all duration-200 ${can ? "group-hover:shadow-md group-hover:border-primary group-active:scale-[0.995]" : ""}`}>
                  <CardContent className="p-5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div
                        className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `hsl(${theme.primary} / 0.12)` }}
                        data-testid={`branch-mark-${b.name.toLowerCase()}`}
                      >
                        <div className="w-6 h-6" style={cocoonMarkStyle(theme.primary)} aria-hidden="true" />
                      </div>
                      <div>
                        <p className="font-medium">Cocoon Medical Spa · {b.name}</p>
                        <p className="text-xs text-muted-foreground">{b.address}</p>
                        <p className="text-xs text-muted-foreground">Hours {formatBranchHours(b)}</p>
                      </div>
                    </div>
                    <ChevronRight className={`w-5 h-5 text-muted-foreground ${can ? "group-hover:text-primary" : ""} transition-colors`} />
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
